import React, { useState, useEffect } from 'react';
import Switch from 'react-switch';

// IMPORTS para el ícono
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';

// IMPORTAR Link para la navegación
import { Link } from 'react-router-dom';

/** 1) Fuentes únicas de los datos de precio y límite de usuarios (en español). */
const basePlans = {
  FREE: {
    priceMonthly: '0€',
    originalPriceMonthly: null,
    priceYearly: '0€',
    originalPriceYearly: null,
    credits: '1 users',
  },
  BASIC: {
    priceMonthly: '25€',
    originalPriceMonthly: '25€',
    priceYearly: '20,84€',
    originalPriceYearly: '25€',
    credits: '1 users',
  },
  PREMIUM: {
    priceMonthly: '90€',
    originalPriceMonthly: '90€',
    priceYearly: '75€',
    originalPriceYearly: '90€',
    credits: 'Up to 20 users',
  },
  BUSINESS: {
    priceMonthly: '160€',
    originalPriceMonthly: '160€',
    priceYearly: '133,35€',
    originalPriceYearly: '160€',
    credits: 'Up to 100 users',
  },
  ENTERPRISE: {
    priceMonthly: 'A medida', // Reemplazaremos por ícono
    originalPriceMonthly: null,
    priceYearly: 'A medida', // Reemplazaremos por ícono
    originalPriceYearly: null,
    credits: 'Unlimited',
  },
};

/** 2) Definimos las traducciones. */
const translations = {
  es: {
    title: 'Precios',
    subtitle:
      'Desde una persona hasta una gran empresa, tenemos un plan para ti.',
    buttonText: 'EMPEZAR',
    footnote: '*Precios sin IVA',
    plans: {
      FREE: {
        name: 'FREE',
        description: 'Para los que se quieren conocer bien y empezar un cambio',
        ...basePlans.FREE,
      },
      BASIC: {
        name: 'PRO',
        description:
          'Para los que quieren herramientas de desarrollo personalizadas',
        ...basePlans.BASIC,
      },
      PREMIUM: {
        name: 'TEAM',
        description: 'Para los equipos exigentes de alto rendimiento',
        popular: true,
        ...basePlans.PREMIUM,
      },
      BUSINESS: {
        name: 'BUSINESS',
        description: 'Para empresas medianas que se preocupan por su talento',
        ...basePlans.BUSINESS,
      },
      ENTERPRISE: {
        name: 'ENTERPRISE',
        description:
          'Para que tus empleados puedan ser mucho más que un número',
        ...basePlans.ENTERPRISE,
      },
    },
  },

  en: {
    title: 'Pricing',
    subtitle:
      'From one individual to one large enterprise, we have a plan for you.',
    buttonText: 'GET STARTED',
    footnote: '*Prices do not include VAT',
    plans: {
      FREE: {
        name: 'FREE',
        description:
          'For those who want to get to know themselves well and start a change.',
        ...basePlans.FREE,
      },
      BASIC: {
        name: 'PRO',
        description:
          'For those who want personalized personal development tools.',
        ...basePlans.BASIC,
      },
      PREMIUM: {
        name: 'TEAM',
        description: 'For high-performance teams with high standards.',
        popular: true,
        ...basePlans.PREMIUM,
      },
      BUSINESS: {
        name: 'BUSINESS',
        description: 'For medium-sized companies that care about their talent.',
        ...basePlans.BUSINESS,
      },
      ENTERPRISE: {
        name: 'ENTERPRISE',
        description: 'So your employees can be much more than just a number.',
        ...basePlans.ENTERPRISE,
      },
    },
  },
};

function Pricing({ language = 'es' }) {
  const [isAnnual, setIsAnnual] = useState(false);

  // Si cambias de idioma, resetear a mensual
  useEffect(() => {
    setIsAnnual(false);
  }, [language]);

  const { title, subtitle, buttonText, footnote, plans } =
    translations[language] || translations.es;

  const planKeys = Object.keys(plans);

  const handleButtonClick = (planName) => {
    // console.log(`Plan clicked: ${planName}`);
  };

  return (
    <section style={styles.section}>
      <h2 style={styles.title}>{title}</h2>
      <p style={styles.subtitle}>{subtitle}</p>

      {/* Toggle */}
      <div style={styles.toggleContainer}>
        <span style={styles.toggleLabel}>
          {language === 'es' ? 'MENSUAL' : 'MONTHLY'}
        </span>
        <Switch
          checked={isAnnual}
          onChange={() => setIsAnnual(!isAnnual)}
          onColor="#4CAF50"
          offColor="#ccc"
          onHandleColor="#fff"
          offHandleColor="#fff"
          checkedIcon={false}
          uncheckedIcon={false}
          handleDiameter={22}
          height={28}
          width={50}
          className="react-switch"
        />
        <span style={styles.toggleLabel}>
          {language === 'es' ? 'ANUAL' : 'YEARLY'}
        </span>
        <span style={styles.discountLabel}>
          {language === 'es' ? '2 MESES GRATIS' : '2 MONTHS FREE'}
        </span>
      </div>

      {/* Tarjetas de planes */}
      <div style={styles.plansContainer}>
        {planKeys.map((key) => {
          const plan = plans[key];
          // Tomamos el precio y el precio original según toggle
          const price = isAnnual ? plan.priceYearly : plan.priceMonthly;
          const originalPrice = isAnnual
            ? plan.originalPriceYearly
            : plan.originalPriceMonthly;

          // Si plan.name es BUSINESS o ENTERPRISE => "Contáctanos / Contact us"
          const isBizOrEnterprise =
            plan.name === 'BUSINESS' || plan.name === 'ENTERPRISE';
          const planButtonText = isBizOrEnterprise
            ? language === 'es'
              ? 'Contáctanos'
              : 'Contact us'
            : buttonText;

          // Si plan.name es ENTERPRISE => icono en vez de "A medida"
          const isEnterprise = plan.name === 'ENTERPRISE';

          return (
            <div
              key={plan.name}
              style={{
                ...styles.planCard,
                ...(plan.popular ? styles.popularPlan : {}),
              }}
            >
              {/* Etiqueta "MÁS POPULAR" */}
              {plan.popular && (
                <div style={styles.popularTag}>
                  {language === 'es' ? 'MÁS POPULAR' : 'MOST POPULAR'}
                </div>
              )}
              {/* Título y descripción */}
              <h3 style={styles.planTitle}>{plan.name}</h3>
              <p style={styles.planDescription}>{plan.description}</p>

              {/* Sección de precio */}
              <div style={styles.priceSection}>
                {/* Reservamos espacio para originalPrice */}
                {originalPrice && originalPrice !== price ? (
                  <p style={styles.originalPrice}>{originalPrice}</p>
                ) : (
                  <p style={styles.emptyLine}></p>
                )}

                {/* Precio principal.
                    Si es ENTERPRISE => mostrar ícono en vez de texto  */}
                {!isEnterprise ? (
                  <p style={styles.planPrice}>
                    {price}
                    <span style={styles.priceSuffix}>
                      {/* siempre "/mes" o "/month", salvo que sea ENTERPRISE */}
                      {language === 'es' ? '/mes' : '/month'}
                    </span>
                  </p>
                ) : (
                  <p style={styles.planPrice}>
                    {/* Ícono en vez de "A medida" */}
                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                  </p>
                )}

                {/* Billed yearly si isAnnual y NO es ENTERPRISE */}
                {isAnnual && !isEnterprise ? (
                  <p style={styles.billedText}>
                    {language === 'es'
                      ? 'Facturado anualmente'
                      : 'Billed yearly'}
                  </p>
                ) : (
                  <p style={styles.emptyLine}></p>
                )}
              </div>

              {/* Aquí usamos Link para redirigir a /register */}
              <Link to="/register">
                <button
                  style={styles.planButton}
                  onClick={() => handleButtonClick(plan.name)}
                >
                  {planButtonText}
                </button>
              </Link>

              <p style={styles.credits}>{plan.credits}</p>
            </div>
          );
        })}
      </div>

      {/* Pie de página centrado */}
      <p style={styles.footnote}>{footnote}</p>
    </section>
  );
}

// Estilos actualizados para evitar fuentes thin (se asegura un fontWeight mínimo de 500)
const styles = {
  section: {
    padding: '50px 20px',
    backgroundColor: '#fff',
  },
  title: {
    fontSize: '36px',
    fontWeight: '600',
    color: '#1C1C3B',
    marginBottom: '10px',
    marginTop: '40px',
    textAlign: 'left',
    paddingLeft: '50px',
  },
  subtitle: {
    fontSize: '18px',
    color: '#555',
    marginBottom: '40px',
    fontWeight: '500', // Se aumenta para evitar fuentes thin
    textAlign: 'left',
    paddingLeft: '55px',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '10px',
    paddingRight: '45px',
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1C1C3B',
  },
  discountLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#4CAF50',
    backgroundColor: '#E8F5E9',
    padding: '6px',
    borderRadius: '15px',
  },
  plansContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '20px',
  },
  planCard: {
    width: '275px',
    minHeight: '100px',
    padding: '40px',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  popularPlan: {
    border: '2px solid #ff2300',
  },
  popularTag: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ff2300',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '6px',
    textTransform: 'uppercase',
    borderRadius: '8px 8px 0 0',
  },
  planTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1C1C3B',
    marginBottom: '5px',
  },
  planDescription: {
    fontSize: '14px',
    color: '#555',
    margin: '10px 0 20px',
    minHeight: '40px',
    textAlign: 'center',
    fontWeight: '400', // Se especifica para evitar fuentes thin
  },
  priceSection: {
    minHeight: '80px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px',
  },
  emptyLine: {
    minHeight: '0.8em',
    margin: 0,
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: '#888',
    fontSize: '16px',
    margin: 0,
    fontWeight: '500', // Se aumenta el peso para evitar fuente thin
  },
  planPrice: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1C1C3B',
    margin: 0,
  },
  priceSuffix: {
    fontSize: '18px',
    fontWeight: '500', // Aseguramos un peso adecuado
    marginLeft: '4px',
    color: '#555',
  },
  billedText: {
    fontSize: '14px',
    color: '#555',
    margin: 0,
    marginBottom: '20px',
    fontWeight: '300', // Se especifica para evitar fuentes thin
  },
  planButton: {
    marginTop: 'auto',
    backgroundColor: '#ff2300',
    color: '#fff',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '30px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    textTransform: 'uppercase',
    transition: 'background-color 0.3s ease',
  },
  credits: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '0px',
    fontWeight: '400', // Se aumenta para evitar fuentes thin
  },
  footnote: {
    fontSize: '12px',
    color: '#888',
    textAlign: 'center',
    fontWeight: '300', // Se aumenta para evitar fuentes thin
  },
};

export default Pricing;
